import { Cross1Icon, PlusIcon } from "@radix-ui/react-icons";
import {
  AccountResponse,
  AccountsService,
  ApiError,
  AvailabilitiesService,
  CalendarService,
  TutorAdminResponse,
} from "client/openapi";
import AddAvailability from "components/AddAvailability";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import ClearAvailability from "components/ClearAvailability";
import { Dialog, DialogContent, DialogTrigger } from "components/Dialog";
import { LoadingBlock } from "components/StatusBlock";
import moment from "moment-timezone";
import { Fragment, useContext, useEffect, useState } from "react";
import { PageStatus } from "types";
import parseAvailability from "util/parseAvailability";
import range from "util/range";
import Notifications from "util/notifications";
import "./index.css";

/**
 * A slot's clock time ("9:00am" for slot 36). Counted on a clock with no
 * daylight saving, so the Sundays when the clocks change label every slot as
 * the time it stands for.
 */
const slotLabel = (slot: number) =>
  moment
    .utc(0)
    .add(slot * 15, "minutes")
    .format("h:mma");

/**
 * Weekly availability. A tutor edits their own; given `tutor`, an admin sees
 * and edits that tutor's (the Availability tab of the tutor's page).
 */
export default function TutorAvailability({
  tutor,
}: { tutor?: TutorAdminResponse } = {}) {
  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [googleCalendarEnabled, setGoogleCalendarEnabled] =
    useState<boolean>(false);

  console.log("googleCalendarEnabled", googleCalendarEnabled);
  const [actuallyRemoveAccount, setActuallyRemoveAccount] =
    useState<boolean>(false);
  const [attemptingUnlink, setAttemptingUnlink] = useState<boolean>(false);
  const [attemptingEnable, setAttemptingEnable] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const [availability, setAvailability] = useState<boolean[]>([]);
  const [timezone, setTimezone] = useState(
    moment.tz.guess(true) || process.env.REACT_APP_DEFAULT_TIMEZONE || "UTC"
  );

  const weekStart = moment().tz(timezone).startOf("isoWeek");

  useEffect(() => {
    if (tutor) {
      AvailabilitiesService.getTutorAvailabilitiesAsAdmin({ tutorId: tutor.id })
        .then((a) => {
          setAvailability(parseAvailability(a, timezone));
          setLoaded(true);
        })
        .catch(() => setLoadFailed(true));
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    AccountsService.getCurrentAccount()
      .then((a) => {
        setAccount(a);
        setLoaded(true);
        if (!code) {
          setGoogleCalendarEnabled(a.google_calendar_enabled || false);
        }
        if (a.availability) {
          const avails = parseAvailability(a.availability[0], timezone);
          setAvailability(avails);
        }
      })
      .catch((e: ApiError) => {
        console.error(`Error (#${e.status}): ${e.message}`);
      });

    if (code) {
      setDialogOpen(true);
      setAttemptingEnable(true);
      CalendarService.enableGoogleCalendar({ code: code })
        .then(() => {
          Notifications.success(
            `Google calendar enabled, your meetings will sync over the next 10 minutes!`
          );
          setGoogleCalendarEnabled(true);
          // Remove code parameter from URL
          urlParams.delete("code");
          const newUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, "", newUrl);
          setAttemptingEnable(false);
        })
        .catch(() => {
          Notifications.error(`Could not enable google calendar at this time.`);
          setAttemptingEnable(false);
        });
    }
  }, [timezone, tutor]);

  async function initiateGoogleCalendarAuthFlow() {
    const auth_url = await CalendarService.getGoogleAuthUrl();
    window.location.href = auth_url;
  }

  async function removeAccount() {
    setAttemptingUnlink(true);
    CalendarService.revokeGoogleCalendar()
      .then(() => {
        Notifications.success("Google calendar unlinked!");
        setGoogleCalendarEnabled(false);
        // Update the account state to reflect the change
        setAttemptingUnlink(false);
      })
      .catch((e: any) => {
        console.error(e);
        Notifications.error("Unable to unlink google calendar at this time");
        setAttemptingUnlink(false);
      });
  }

  return (
    <div className="container mx-auto page--tutor-availability">
      {loadFailed && (
        <p className="mt-5 text-sm text-red-600">
          This tutor's availability could not be loaded.
        </p>
      )}
      {!loaded && !loadFailed && <LoadingBlock status={PageStatus.LOADING} />}

      {loaded && (
        <>
          <div className="page--header">
            <h1 className="section--title">
              {tutor
                ? `${tutor.first_name}'s weekly availability`
                : "Set Weekly Availability"}
            </h1>
          </div>

          {/* Google Calendar is the tutor's own link. */}
          {!tutor && (
            <div className="mb-4">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button color={ButtonColor.PURPLE} size={ButtonSize.SMALL}>
                    Google Calendar
                  </Button>
                </DialogTrigger>

                <DialogContent
                  className="google-dialog-content"
                  onClose={() => {
                    setActuallyRemoveAccount(false);
                    setDialogOpen(false);
                  }}
                >
                  {account && (
                    <div>
                      <h1 className="heading">Google Calendar</h1>
                      {attemptingEnable || attemptingUnlink ? (
                        <div className="mt-3 d-flex align-items-center">
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          <span>
                            {attemptingEnable
                              ? "Enabling Google Calendar..."
                              : "Unlinking Google Calendar..."}
                          </span>
                        </div>
                      ) : googleCalendarEnabled ||
                        new URLSearchParams(window.location.search).get(
                          "code"
                        ) ? (
                        <div>
                          <div>
                            Your account is synced with a Google Calendar
                            account.
                          </div>
                          <div className="mt-5">
                            {actuallyRemoveAccount ? (
                              <Button
                                color={
                                  attemptingUnlink
                                    ? ButtonColor.GRAY
                                    : ButtonColor.RED
                                }
                                size={ButtonSize.SMALL}
                                disabled={attemptingUnlink}
                                onClick={() => removeAccount()}
                              >
                                Confirm account removal
                              </Button>
                            ) : (
                              <Button
                                color={ButtonColor.SKYBLUE}
                                size={ButtonSize.SMALL}
                                onClick={() => {
                                  setActuallyRemoveAccount(true);
                                }}
                              >
                                Remove account?
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3">
                          <Button
                            color={ButtonColor.PURPLE}
                            size={ButtonSize.SMALL}
                            onClick={initiateGoogleCalendarAuthFlow}
                          >
                            Sync google calendar
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="schedule">
            {range(7).map((d) => {
              const avails = availability?.filter((a, i) => {
                return i >= d * 96 && i < (d + 1) * 96;
              });

              return (
                <div className="schedule--day" key={d}>
                  <div className="date">
                    {moment(weekStart).add(d, "days").format("dddd")}

                    <div className="date--actions">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            color={ButtonColor.RED}
                            fill={ButtonFill.HOLLOW}
                            extraClasses="button--manage-day"
                            aria-label={`Clear all availability for ${moment(
                              weekStart
                            )
                              .add(d, "days")
                              .format("dddd")}`}
                          >
                            <Cross1Icon />
                          </Button>
                        </DialogTrigger>

                        <ClearAvailability
                          day={moment(weekStart)
                            .add(d, "days")
                            .format("ddd")
                            .toLowerCase()}
                          timezone={timezone}
                          setAvailability={setAvailability}
                          tutorId={tutor?.id}
                        />
                      </Dialog>

                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            color={ButtonColor.ORANGE}
                            extraClasses="button--manage-day"
                            aria-label={`Add availability for ${moment(
                              weekStart
                            )
                              .add(d, "days")
                              .format("dddd")}`}
                          >
                            <PlusIcon />
                          </Button>
                        </DialogTrigger>

                        <AddAvailability
                          day={moment(weekStart)
                            .add(d, "days")
                            .format("ddd")
                            .toLowerCase()}
                          timezone={timezone}
                          setAvailability={setAvailability}
                          tutorId={tutor?.id}
                        />
                      </Dialog>
                    </div>
                  </div>

                  <div className="availabilities">
                    {avails?.filter((a) => a).length === 0 ? (
                      <div className="availability--none">No availability</div>
                    ) : (
                      avails?.map((a, i) => (
                        <Fragment key={i}>
                          {a ? (
                            <div className="availability--free">
                              {slotLabel(i)}
                            </div>
                          ) : (
                            <div className="availability--busy" hidden>
                              {slotLabel(i)}
                            </div>
                          )}
                        </Fragment>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="timezone">Showing times in: {timezone}</div>
        </>
      )}
    </div>
  );
}
